import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

/**
 * Lịch sử TẤT CẢ ván game xúc xắc của hệ thống; bấm một ván sẽ mở modal danh sách người chơi trong ván.
 *
 * Trang này trước đây đọc /rest/dice-game/get-dice-game-info (kind DiceGameInfoHistory /
 * DiceGamePlayerHistory, do servlet GAE /v43.LoggingDiceGame ghi khi Cloud Function gọi). Từ khi cờ
 * engineClock bật, CF ngừng chạy nên hai kind đó đứng yên và trang luôn trống. Nay đọc thẳng dữ liệu
 * engine (service luckydice): kind DiceGame + UserBettingLog.
 */

const API = "/luckydice/web/dice-history";

function formatTime(millis) {
  if (!millis) return "";
  return new Date(millis).toLocaleString("vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatIcoin(value) {
  if (value === null || value === undefined) return "";
  return value.toLocaleString("vi-VN");
}

/** Thông điệp lỗi thật do backend trả về (DiceHistoryExceptionHandler), nếu có. */
function serverMessage(err) {
  return err && err.response && err.response.data ? err.response.data.message : null;
}

const PAGE_SIZE = 200;

function DiceGameInfoHistory() {
  const [selectedDate, setSelectedDate] = useState("");
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [compensating, setCompensating] = useState(null);

  /** Body dùng chung cho tìm mới lẫn tải thêm — giữ nguyên ngày đang chọn khi sang trang. */
  function buildBody(cursor) {
    // input type=date trả "YYYY-MM-DD" theo giờ local của máy admin → lấy nguyên ngày đó.
    const start = new Date(selectedDate);
    start.setHours(0, 0, 0, 0);
    const body = {
      startDateMillis: start.getTime(),
      endDateMillis: start.getTime() + 24 * 3600 * 1000,
      limit: PAGE_SIZE,
    };
    if (cursor) body.cursor = cursor;
    return body;
  }

  async function fetchRounds() {
    if (!selectedDate) {
      toast.warn("Vui lòng chọn ngày cần kiểm tra");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(global.config.apiDomain + API + "/rounds", buildBody(null));
      setPage(res.data);
      if (!res.data.rounds || res.data.rounds.length === 0) {
        toast.info("Ngày này không có ván nào");
      }
    } catch (err) {
      setPage(null);
      toast.error(serverMessage(err) || "Lỗi khi tải danh sách ván");
    } finally {
      setLoading(false);
    }
  }

  /** Nối trang kế vào cuối bảng, giữ nguyên phần đã tải. */
  async function loadMore() {
    if (!page || !page.nextCursor) return;
    setLoadingMore(true);
    try {
      const res = await axios.post(global.config.apiDomain + API + "/rounds", buildBody(page.nextCursor));
      setPage({
        ...res.data,
        rounds: (page.rounds || []).concat(res.data.rounds || []),
      });
    } catch (err) {
      toast.error(serverMessage(err) || "Lỗi khi tải thêm");
    } finally {
      setLoadingMore(false);
    }
  }

  async function openDetail(gameId) {
    setDetail({ round: { gameId: gameId }, players: [] });
    setDetailLoading(true);
    try {
      const res = await axios.post(global.config.apiDomain + API + "/round-players", { gameId: gameId });
      setDetail(res.data);
    } catch (err) {
      toast.error(
        err.response && err.response.status === 404
          ? "Ván này không có dữ liệu người chơi"
          : serverMessage(err) || "Lỗi khi tải chi tiết ván"
      );
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }

  async function compensate(player) {
    if (!window.confirm(
      "Nạp bù cho " + (player.name || player.userId) + " ở ván " + detail.round.gameId + "?\n\n" +
      "Hệ thống tự tính lại tiền thắng từ số cược của ván. Bấm nhiều lần cũng chỉ cộng một lần."
    )) {
      return;
    }
    setCompensating(player.userId);
    try {
      const res = await axios.post(global.config.apiDomain + API + "/compensate", {
        gameId: detail.round.gameId,
        userId: player.userId,
      });
      if (res.data.ok) {
        toast.success(res.data.message);
        openDetail(detail.round.gameId); // nạp lại để cột trạng thái phản ánh đúng
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(
        err.response && err.response.status === 403
          ? "Bạn không có quyền nạp bù (cần ICOIN-MANAGER)"
          : "Nạp bù thất bại"
      );
    } finally {
      setCompensating(null);
    }
  }

  return (
    <div className="container" style={{ marginTop: "100px", marginBottom: "100px" }}>
      <h1 className="text-center">Lịch sử ván game xúc xắc</h1>

      <div className="form-inline justify-content-center" style={{ marginTop: "30px" }}>
        <label htmlFor="dice-date" style={{ paddingRight: "15px" }}>Chọn ngày cần kiểm tra</label>
        <input
          id="dice-date"
          type="date"
          className="form-control"
          style={{ marginRight: "15px" }}
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
        />
        <button className="btn btn-primary" onClick={fetchRounds} disabled={loading}>
          {loading ? "Đang tải..." : "Tìm kiếm"}
        </button>
      </div>

      {page === null ? (
        <h5 className="text-center" style={{ marginTop: "40px" }}>Chọn ngày để tra cứu dữ liệu</h5>
      ) : (
        <>
        <p className="text-muted" style={{ marginTop: "25px" }}>
          Đang hiển thị {page.rounds ? page.rounds.length : 0} ván{page.hasMore ? ", còn nữa" : ""}.
        </p>
        <table className="table table-bordered table-hover">
          <thead className="table-primary">
            <tr>
              <th>No</th>
              <th>Id ván game</th>
              <th>Thời gian</th>
              <th>Kết quả</th>
              <th>Chi tiết</th>
            </tr>
          </thead>
          <tbody>
            {page.rounds && page.rounds.length > 0 ? (
              page.rounds.map((round, index) => (
                <tr key={round.gameId}>
                  <td>{index + 1}</td>
                  <td>{round.gameId}</td>
                  <td>{formatTime(round.dateTime)}</td>
                  <td>{round.resultText || "—"}</td>
                  <td>
                    <button className="btn btn-sm btn-info" onClick={() => openDetail(round.gameId)}>
                      Chi tiết
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="text-center">Không có dữ liệu</td>
              </tr>
            )}
          </tbody>
        </table>

        {page.hasMore && (
          <div className="text-center">
            <button className="btn btn-outline-primary" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? "Đang tải..." : "Tải thêm"}
            </button>
          </div>
        )}
        </>
      )}

      {detail && (
        <div className="modal fade show" tabIndex="-1" style={{ display: "block" }} role="dialog">
          <div className="modal-dialog modal-lg modal-dialog-scrollable" style={{ maxWidth: "90%" }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Người chơi trong ván {detail.round.gameId}
                  {detail.round.resultText ? " — " + detail.round.resultText : ""}
                </h5>
                <button type="button" className="close" aria-label="Close" onClick={() => setDetail(null)}>
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div className="modal-body">
                {detailLoading ? (
                  <h5>Đang tải dữ liệu...</h5>
                ) : (
                  <table className="table table-bordered table-hover">
                    <thead className="table-primary">
                      <tr>
                        <th>No</th>
                        <th>uid</th>
                        <th>userId</th>
                        <th>Name</th>
                        <th>BIG</th>
                        <th>SMALL</th>
                        <th>TRIPLE</th>
                        <th>Thắng/Thua</th>
                        <th>Cộng tiền</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.players && detail.players.length > 0 ? (
                        detail.players.map((player, index) => (
                          <tr key={player.userId}>
                            <td>{index + 1}</td>
                            <td>{player.uid}</td>
                            <td>{player.userId}</td>
                            <td>{player.name}</td>
                            <td>{formatIcoin(player.icoinBig)}</td>
                            <td>{formatIcoin(player.icoinSmall)}</td>
                            <td>{formatIcoin(player.icoinTriple)}</td>
                            <td style={{ color: player.icoinResult < 0 ? "#dc3545" : "#28a745" }}>
                              {formatIcoin(player.icoinResult)}
                            </td>
                            <td>
                              {player.status === "FAILED" ? (
                                <span className="badge badge-danger">LỖI</span>
                              ) : (
                                <span className="badge badge-success">OK</span>
                              )}
                            </td>
                            <td>
                              {player.canCompensate && (
                                <button
                                  className="btn btn-sm btn-warning"
                                  onClick={() => compensate(player)}
                                  disabled={compensating === player.userId}
                                >
                                  {compensating === player.userId ? "Đang nạp..." : "Nạp bù"}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={10} className="text-center">Không có dữ liệu</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setDetail(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DiceGameInfoHistory;
